import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import getIsServer from '~/app/utils/getIsServer'
import type { YaNative } from '@yanolja-rnd/ya-native'
import { getYaNative } from '@yanolja-rnd/ya-native'

/**
 * useModalHash
 *
 * 해시 기반 모달 상태 관리 훅
 * - Native History API를 사용하여 Next.js 라우터 오버헤드 방지
 * - 자동 고유 ID 생성으로 충돌 방지
 * - iOS 네이티브 앱 스와이프백 제스처 지원
 *
 */

/**
 * iOS 네이티브 앱에서 스와이프백 제스처를 제어하기 위한 옵션
 */
export type SwipeBackOption = {
  /** 모달이 열릴 때 호출 - 스와이프백을 활성화 */
  onOpen: () => void
  /** 모달이 닫힐 때 호출 - 스와이프백을 비활성화 */
  onClose?: () => void
}

/**
 * 네이티브 앱 환경 체크
 */
const yaNative = getIsServer() ? ({} as YaNative) : getYaNative()

const idMap: Record<string, boolean> = {}

type Hook = (param: {
  id: string
  swipeBackOption?: SwipeBackOption
}) => [boolean, (isOpened: boolean) => void]

// hook
const useModalHash: Hook = function ({ id, swipeBackOption }) {
  const uniqueId = useId().replace(/:/g, '')
  const modalId = useMemo(() => `${id}-${uniqueId}`, [id, uniqueId])

  const getHashInfo = (modalId: string) => {
    const hash = window.location.hash
    const hashStack = hash.substring(1).split('|')
    const shouldOpen = hashStack.filter(dialogId => dialogId === modalId).length > 0
    return {
      hash,
      hashStack,
      shouldOpen,
    }
  }

  const [isOpened, setOpened] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false
    }
    return getHashInfo(modalId).shouldOpen
  })

  useEffect(() => {
    if (idMap[modalId]) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          `[warning] Modal ID 에 중복이 있습니다. 중복된 ID 는 "${modalId}" 입니다. 잘못 설정된 것이 아닌지 다시 확인해 주세요.`
        )
      }
    }
    idMap[modalId] = true
    return () => void (idMap[modalId] = false)
  }, [modalId])

  useEffect(() => {
    // popstate 이벤트 리스닝 (history.pushState/back으로 인한 해시 변경)
    // 참고: pushState/replaceState는 이 이벤트를 발생시키지 않으므로 수동으로 setState를 호출해야 함
    const onPopState = () => {
      setOpened(getHashInfo(modalId).shouldOpen)
    }
    window.addEventListener('popstate', onPopState)

    return () => {
      window.removeEventListener('popstate', onPopState)
    }
  }, [modalId])

  const setState = useCallback(
    (isOpened: boolean) => {
      const { hashStack, shouldOpen: isExist } = getHashInfo(modalId)

      /**
       * iOS 네이티브 앱 스와이프백 제스처 제어
       *
       * 1. 스와이프백 활성화 시점:
       *    - 모달을 열 때 (isOpened = true)
       *    - 현재 해시 스택이 비어있을 때 (첫 번째 모달)
       *    → 이제 첫 모달이 열리므로 스와이프백을 활성화
       *
       * 2. 스와이프백 비활성화 시점:
       *    - 모달을 닫을 때 (isOpened = false)
       *    - 현재 해시 스택에 모달이 1개만 있을 때
       *    → 마지막 모달이 닫히므로 스와이프백을 비활성화
       */
      const hasIdHashStackLength = hashStack.filter(t => !!t).length
      if (yaNative.isEnabled && swipeBackOption) {
        if (isOpened && !hasIdHashStackLength) {
          // 첫 번째 모달이 열림 → 스와이프백 활성화
          swipeBackOption.onOpen()
        } else if (!isOpened && isExist && hasIdHashStackLength <= 1) {
          // 마지막 모달이 닫힘 → 스와이프백 비활성화
          swipeBackOption.onClose?.()
        }
      }

      if (isOpened) {
        // 열때는 url 에 중복이 있는지 여부를 보고 해시에 아이디가 존재하지 않으면 추가한다
        if (!isExist) {
          const newHash = [...hashStack, modalId].filter(modalId => !!modalId).join('|')
          // history.pushState를 사용하여 해시만 변경 (Next.js 라우터를 거치지 않아 서버 렌더링 방지)
          window.history.pushState(window.history.state, '', `#${newHash}`)
          // pushState는 hashchange 이벤트를 발생시키지 않으므로 직접 상태 업데이트
          setOpened(true)
        } else {
          // 이미 해시에 존재하는 경우에도 상태를 업데이트
          setOpened(true)
        }
      } else {
        // 닫을 때는 해시 스택에서 해당 모달 ID를 제거
        // history.back() 대신 해시 스택을 직접 조작하여 Next.js 라우터와 호환성 유지
        if (isExist) {
          const newHashStack = hashStack.filter(dialogId => dialogId !== modalId)
          const newHash = newHashStack.filter(modalId => !!modalId).join('|')
          // history.pushState를 사용하여 해시만 변경
          const newUrl = newHash
            ? `${window.location.pathname}${window.location.search}#${newHash}`
            : `${window.location.pathname}${window.location.search}`
          window.history.back()
          window.history.replaceState(window.history.state, '', newUrl)
        } else {
          // 이미 해시에 존재하지 않는 경우에도 상태를 업데이트
          setOpened(false)
        }
      }
    },
    [modalId, swipeBackOption]
  )

  return [isOpened, setState]
}

export default useModalHash
